import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

const FieldServiceReportModal = ({ isOpen, onClose, user, axiosInstance, initialData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    account_name: '',
    address: '',
    contact_person: '',
    contact_number: '',
    call_received_by: '',
    date_called: '',
    time_called: '',
    date: new Date().toISOString().split('T')[0],
    time_in: '',
    time_out: '',
    model: '',
    serial_number: '',
    meter_reading_total: 0,
    defect_reported: '',
    last_consumables_order: '',
    work_done: '',
    inspection_checklist: {},
    parts_replaced: [],
    remarks: '',
    status: 'Pending',
    customer_signature: ''
  });

  React.useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          ...initialData,
          // Ensure arrays/objects are properly initialized if null
          inspection_checklist: initialData.inspection_checklist || {},
          parts_replaced: initialData.parts_replaced || [],
          // Ensure numeric fields are numbers
          meter_reading_total: initialData.meter_reading_total || 0,
        });
      } else {
        // Reset form for new entry
        setFormData({
          account_name: '',
          email: '',
          address: '',
          contact_person: '',
          contact_number: '',
          call_received_by: '',
          date_called: '',
          time_called: '',
          date: new Date().toISOString().split('T')[0],
          time_in: '',
          time_out: '',
          model: '',
          serial_number: '',
          meter_reading_total: 0,
          defect_reported: '',
          last_consumables_order: '',
          work_done: '',
          inspection_checklist: {},
          parts_replaced: [],
          remarks: '',
          status: 'Pending',
          customer_signature: ''
        });
      }
    }
  }, [isOpen, initialData]);

  const [isSignaturePadOpen, setIsSignaturePadOpen] = useState(false);
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      
      const formData = new FormData();
      formData.append('file', blob, 'signature.png');
      
      try {
        const response = await axiosInstance.post('/upload-signature', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        setFormData(prev => ({
          ...prev,
          customer_signature: response.data.url
        }));
        setIsSignaturePadOpen(false);
        toast.success('Signature saved successfully');
      } catch (error) {
        console.error('Error uploading signature:', error);
        toast.error('Failed to save signature');
      }
    }, 'image/png');
  };

  const inspectionItems = [
    { category: 'Document Feeder', items: ['Platen Roller', 'ADF pick-up roller', 'ADF feed roller', 'ADF separation pad'] },
    { category: 'Light Path', items: ['Copy board glass', 'Standard white plate', 'Lamp', 'Lens', 'Scanner Assembly'] },
    { category: 'Image Formation', items: ['Reading unit-upper', 'Reading unit-lower', 'Photodrum', 'Primary charging assembly', 'Developing assembly', 'Inter Transfer Belt', 'Transfer charging assembly', 'Drum/ITB cleaning assembly', 'Laser Assembly'] },
    { category: 'Fixing', items: ['Fixing roller/film unit', 'Pressure Roller', 'Cleaning Belt', 'Seperation claws'] },
    { category: 'Paper Path', items: ['Pick-up rollers', 'Separation rollers/pad', 'Delivery rollers', 'Duplex rollers'] },
    { category: 'Others', items: ['Waste toner receptacle'] }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMeterReadingChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseInt(value) || 0
    }));
  };

  const handleChecklistChange = (item, status) => {
    setFormData(prev => ({
      ...prev,
      inspection_checklist: {
        ...prev.inspection_checklist,
        [item]: status
      }
    }));
  };

  const handleAddPart = () => {
    setFormData(prev => ({
      ...prev,
      parts_replaced: [...prev.parts_replaced, { name: '', quantity: 1, part_status: 'Replaced' }]
    }));
  };

  const handlePartChange = (index, field, value) => {
    const newParts = [...formData.parts_replaced];
    newParts[index][field] = field === 'quantity' ? parseInt(value) || 0 : value;
    setFormData(prev => ({
      ...prev,
      parts_replaced: newParts
    }));
  };

  const handleRemovePart = (index) => {
    const newParts = formData.parts_replaced.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      parts_replaced: newParts
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.account_name || !formData.model || !formData.serial_number) {
      toast.error('Please fill in all required fields (Account Name, Model, Serial Number)');
      return;
    }

    setIsSubmitting(true);
    try {
      // Ensure all checklist items have a value, defaulting to 'N/A'
      const completeChecklist = { ...formData.inspection_checklist };
      inspectionItems.forEach(category => {
        category.items.forEach(item => {
          if (!completeChecklist[item]) {
            completeChecklist[item] = 'N/A';
          }
        });
      });

      const dataToSubmit = {
        ...formData,
        inspection_checklist: completeChecklist
      };

      // Clean up payload to match Pydantic schema
      // Remove fields that might be present from initialData but not in Create/Update schema
      const { 
        id, _id, fsr_number, technician_id, technician_name, 
        branch, createdAt, updatedAt, 
        ...cleanData 
      } = dataToSubmit;
      
      // Ensure parts_replaced has correct structure
      if (cleanData.parts_replaced) {
        cleanData.parts_replaced = cleanData.parts_replaced.map(part => ({
          name: part.name,
          quantity: part.quantity,
          part_status: part.part_status || 'Replaced'
        }));
      }

      // Explicitly set updated parts_replaced
      const finalDataToSubmit = {
        ...cleanData,
        parts_replaced: cleanData.parts_replaced
      };

      if (initialData && initialData.id) {
        // Update existing report
        await axiosInstance.put(`/field-service-reports/${initialData.id}`, finalDataToSubmit);
        toast.success('Field Service Report updated successfully');
      } else {
        // Create new report
        await axiosInstance.post('/field-service-reports', finalDataToSubmit);
        toast.success('Field Service Report created successfully');
      }
      
      onClose();
      // Reset form handled by useEffect
    } catch (error) {
      console.error('Error saving FSR:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save Field Service Report';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Field Service Report</DialogTitle>
          <DialogDescription>
            Create a new service report for {user.branch} branch
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Header Info - Call Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <Label>Call Received By</Label>
              <Input name="call_received_by" value={formData.call_received_by} onChange={handleInputChange} />
            </div>
            <div>
              <Label>Date Called</Label>
              <Input type="date" name="date_called" value={formData.date_called} onChange={handleInputChange} />
            </div>
            <div>
              <Label>Time Called</Label>
              <Input type="time" name="time_called" value={formData.time_called} onChange={handleInputChange} />
            </div>
          </div>

          {/* Customer & Machine Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Customer Information</h3>
              <div>
                <Label>Account Name *</Label>
                <Input name="account_name" value={formData.account_name} onChange={handleInputChange} required />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" name="email" value={formData.email} onChange={handleInputChange} required />
              </div>
              <div>
                <Label>Address</Label>
                <Input name="address" value={formData.address} onChange={handleInputChange} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact Person</Label>
                  <Input name="contact_person" value={formData.contact_person} onChange={handleInputChange} />
                </div>
                <div>
                  <Label>Contact Number</Label>
                  <Input name="contact_number" value={formData.contact_number} onChange={handleInputChange} />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Machine Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Model Name *</Label>
                  <Input name="model" value={formData.model} onChange={handleInputChange} required />
                </div>
                <div>
                  <Label>Serial No. *</Label>
                  <Input name="serial_number" value={formData.serial_number} onChange={handleInputChange} required />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <Label className="text-xs">Present Meter Reading</Label>
                  <Input type="number" name="meter_reading_total" value={formData.meter_reading_total} onChange={handleMeterReadingChange} />
                </div>
              </div>
            </div>
          </div>

          {/* Technical Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg border-b pb-2">Service Details</h3>
            <div>
              <Label>Defect Reported</Label>
              <Textarea name="defect_reported" value={formData.defect_reported} onChange={handleInputChange} rows={2} />
            </div>
            <div>
              <Label>Last Consumables Order</Label>
              <Textarea name="last_consumables_order" value={formData.last_consumables_order} onChange={handleInputChange} rows={2} />
            </div>
            <div>
              <Label>Work Done</Label>
              <Textarea name="work_done" value={formData.work_done} onChange={handleInputChange} rows={3} />
            </div>
          </div>

          {/* Inspection Checklist */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg border-b pb-2">Inspection Checklist</h3>
            <div className="overflow-x-auto border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Category</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-center w-[80px]">Good</TableHead>
                    <TableHead className="text-center w-[80px]">Faulty</TableHead>
                    <TableHead className="text-center w-[80px]">N/A</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inspectionItems.map((category) => (
                    category.items.map((item, index) => (
                      <TableRow key={item}>
                        {index === 0 && (
                          <TableCell rowSpan={category.items.length} className="font-medium bg-gray-50">
                            {category.category}
                          </TableCell>
                        )}
                        <TableCell>{item}</TableCell>
                        <TableCell className="text-center">
                          <input
                            type="radio"
                            name={`checklist-${item}`}
                            checked={formData.inspection_checklist[item] === 'Good'}
                            onChange={() => handleChecklistChange(item, 'Good')}
                            className="h-4 w-4 text-green-600 focus:ring-green-500"
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <input
                            type="radio"
                            name={`checklist-${item}`}
                            checked={formData.inspection_checklist[item] === 'Faulty'}
                            onChange={() => handleChecklistChange(item, 'Faulty')}
                            className="h-4 w-4 text-red-600 focus:ring-red-500"
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <input
                            type="radio"
                            name={`checklist-${item}`}
                            checked={formData.inspection_checklist[item] === 'N/A' || !formData.inspection_checklist[item]}
                            onChange={() => handleChecklistChange(item, 'N/A')}
                            className="h-4 w-4 text-gray-400 focus:ring-gray-500"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Parts List */}
          <div className="space-y-4">
            <div className="flex justify-between items-center border-b pb-2">
              <h3 className="font-semibold text-lg">Parts List</h3>
              <Button type="button" onClick={handleAddPart} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Part
              </Button>
            </div>
            {formData.parts_replaced.length > 0 ? (
              <div className="space-y-4">
                {formData.parts_replaced.map((part, index) => (
                  <div key={index} className="flex flex-col md:flex-row gap-4 items-start md:items-center p-4 bg-gray-50 rounded-lg border">
                    <div className="flex-1 w-full">
                      <Label className="mb-1 block text-xs">Part Name / Description</Label>
                      <Input
                        placeholder="Part Name"
                        value={part.name}
                        onChange={(e) => handlePartChange(index, 'name', e.target.value)}
                      />
                    </div>
                    <div className="w-full md:w-24">
                      <Label className="mb-1 block text-xs">Qty</Label>
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={part.quantity}
                        onChange={(e) => handlePartChange(index, 'quantity', e.target.value)}
                        min="1"
                      />
                    </div>
                    
                    {/* Status Checklist */}
                    <div className="flex flex-col gap-2 min-w-[150px]">
                      <Label className="mb-1 block text-xs">Status</Label>
                      <div className="flex gap-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`part-status-${index}`}
                            checked={part.part_status === 'Replaced' || !part.part_status}
                            onChange={() => handlePartChange(index, 'part_status', 'Replaced')}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-sm">Replaced</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`part-status-${index}`}
                            checked={part.part_status === 'For Replacement'}
                            onChange={() => handlePartChange(index, 'part_status', 'For Replacement')}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500"
                          />
                          <span className="text-sm">For Replacement</span>
                        </label>
                      </div>
                    </div>

                    <Button type="button" onClick={() => handleRemovePart(index)} variant="ghost" size="icon" className="text-red-500 mt-4 md:mt-0">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded">No parts added</p>
            )}
          </div>

          {/* Service Time Info - Moved below Parts Replaced */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <Label>Date</Label>
              <Input type="date" name="date" value={formData.date} onChange={handleInputChange} required />
            </div>
            <div>
              <Label>Time In</Label>
              <Input type="time" name="time_in" value={formData.time_in} onChange={handleInputChange} />
            </div>
            <div>
              <Label>Time Out</Label>
              <Input type="time" name="time_out" value={formData.time_out} onChange={handleInputChange} />
            </div>
          </div>

          {/* Customer Signature */}
          <div className="space-y-4">
            <h3 className="font-semibold text-lg border-b pb-2">Customer Signature</h3>
            {formData.customer_signature ? (
              <div className="border rounded p-4 flex flex-col items-center gap-2">
                <img 
                  src={formData.customer_signature.startsWith('http') ? formData.customer_signature : `${BACKEND_URL}${formData.customer_signature}`} 
                  alt="Customer Signature" 
                  className="max-h-32 object-contain" 
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setFormData(prev => ({ ...prev, customer_signature: '' }))}
                >
                  Clear Signature
                </Button>
              </div>
            ) : (
              <Button type="button" onClick={() => setIsSignaturePadOpen(true)} variant="outline">
                Add Customer Signature
              </Button>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {isSubmitting ? 'Saving...' : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Report
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* Signature Pad Dialog */}
      <Dialog open={isSignaturePadOpen} onOpenChange={setIsSignaturePadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Customer Signature</DialogTitle>
            <DialogDescription>Please sign in the box below</DialogDescription>
          </DialogHeader>
          <div className="border-2 border-gray-300 rounded bg-white">
            <canvas
              ref={canvasRef}
              width={400}
              height={200}
              className="w-full touch-none"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={clearSignature}>
              Clear
            </Button>
            <Button type="button" onClick={saveSignature} className="bg-indigo-600 text-white">
              Save Signature
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default FieldServiceReportModal;
